import {
  buildConnectorInfo,
  buildFileTypeInfo,
} from "@mirage/service-dbx-api/service/utils";
import { tagged } from "@mirage/service-logging";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import i18n from "@mirage/translations";

import type { dash, dash_feed } from "@dropbox/api-v2-client";
import type { LaunchMethod } from "@mirage/analytics/events/enums/launch_method";
import type * as dbxApiService from "@mirage/service-dbx-api";
import type { RecentContent } from "@mirage/service-recent-content/types";
import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import type { QuestionAndAnswerSource } from "@mirage/shared/answers/multi-answer";
import type {
  ConnectorInfo,
  FileTypeInfo,
  SearchResult,
  SurfaceSpecificRecordType,
} from "@mirage/shared/search/search-result";
import { useCallback } from "react";
import type { TypeaheadResult } from "../SearchBarWithTypeahead/useConvertToTypeaheadResults";

// TODO OSE-4166 Move this file and tests to it's own package

const logger = tagged("SummaryQnaPanel/utils");

export enum SummaryQnAEnum {
  ACTIVITY = "summarizable-activity",
  QNA = "summarizable-qna",
  SEARCH = "summarizable-search",
  STACK_ITEM = "summarizable-stack-item",
  TYPEAHEAD = "summarizable-typeahead",
  RECENT = "summarizable-recent",
}

// import type { SummarizableResult } from '@mirage/mosaics/SummaryQnaPanel/utils';
export type SummarizableResult = {
  type: SummaryQnAEnum;
  summarizableTag?: dash.SummarizableType[".tag"] | null;
  uuid: string;
  url: string | null;
  title: JSX.Element | string;
  connectorInfo: ConnectorInfo | null;
  fileTypeInfo: FileTypeInfo | null;
  icon: JSX.Element | string | null;
  launchResult: ((launchMethod: LaunchMethod) => void) | null;
  recordType?: RecordTypeTag;
  searchRequestId?: string;
};

export type NormalizeResultInputType =
  | dash_feed.ActivityObject
  | QuestionAndAnswerSource
  | SearchResult
  | TypeaheadResult
  | StackItemShortcutWithMetadata
  | RecentContent;

export const normalizeResultForSummary = (
  result: NormalizeResultInputType,
  summaryQnAType: SummaryQnAEnum,
  isDarkMode: boolean = false, // TODO: SEAN - update call sites
): SummarizableResult | null => {
  const stackItemShortcut = result as StackItemShortcutWithMetadata;
  switch (summaryQnAType) {
    case SummaryQnAEnum.ACTIVITY: {
      const activityObject: dash_feed.ActivityObject =
        result as dash_feed.ActivityObject;
      let connectorInfoOnActivityObject = activityObject.connector_info;

      if (
        !connectorInfoOnActivityObject &&
        activityObject.details?.object_details?.[".tag"] === "document"
      ) {
        connectorInfoOnActivityObject =
          activityObject.details.object_details.connector_info;
      }
      let fileTypeInfoOnActivityObject = activityObject.file_type_info;
      if (
        !fileTypeInfoOnActivityObject &&
        activityObject.details?.object_details?.[".tag"] === "document"
      ) {
        fileTypeInfoOnActivityObject =
          activityObject.details.object_details.file_type_info;
      }
      return {
        type: summaryQnAType,
        summarizableTag:
          (result as dash_feed.ActivityObject).summarizable?.[".tag"] || null,
        uuid: (result as dash_feed.ActivityObject).uuid || "",
        url: (result as dash_feed.ActivityObject).url || null,
        title: (result as dash_feed.ActivityObject).name || "",
        connectorInfo:
          buildConnectorInfo(connectorInfoOnActivityObject) || null,
        fileTypeInfo: buildFileTypeInfo(fileTypeInfoOnActivityObject) || null,
        icon: null,
        launchResult: null,
      };
    }
    case SummaryQnAEnum.QNA:
      return {
        type: summaryQnAType,
        summarizableTag: null,
        uuid: (result as QuestionAndAnswerSource).id3p,
        url: (result as QuestionAndAnswerSource).url || null,
        title: (result as QuestionAndAnswerSource).title,
        connectorInfo:
          buildConnectorInfo({
            ...(result as QuestionAndAnswerSource).connectorInfo,
            connector_type: { ".tag": "other" },
            platform: undefined,
          }) || null,
        fileTypeInfo: (result as QuestionAndAnswerSource).fileTypeInfo || null,
        icon: (result as QuestionAndAnswerSource).iconUrl || null,
        launchResult: null,
      };
    case SummaryQnAEnum.SEARCH:
      return {
        type: summaryQnAType,
        summarizableTag: (result as SearchResult).summarizable || null,
        uuid: (result as SearchResult).uuid,
        url: (result as SearchResult).url || null,
        title: (result as SearchResult).title,
        recordType: getRecordType(result as SearchResult),
        connectorInfo: (result as SearchResult).connectorInfo || null,
        fileTypeInfo: (result as SearchResult).fileTypeInfo || null,
        icon: null,
        launchResult: null,
        searchRequestId: (result as SearchResult).searchRequestId || "",
      };
    case SummaryQnAEnum.STACK_ITEM:
      if (!stackItemShortcut.url) {
        throw new Error("URL is required for StackItemShortcutWithMetadata");
      }
      return {
        type: summaryQnAType,
        summarizableTag: stackItemShortcut.summarizableTag || null,
        uuid: stackItemShortcut.uuid || "",
        url: stackItemShortcut.url,
        title:
          stackItemShortcut.metadata_title ||
          stackItemShortcut.custom_name ||
          stackItemShortcut.name ||
          "",
        connectorInfo: null,
        fileTypeInfo: null,
        icon: faviconSrcForSrcUrl(stackItemShortcut.url, 32, isDarkMode),
        launchResult: null,
      };
    case SummaryQnAEnum.TYPEAHEAD:
      return {
        type: summaryQnAType,
        summarizableTag: (result as TypeaheadResult).summarizable || null,
        uuid: (result as TypeaheadResult).uuid,
        url: null,
        title: (result as TypeaheadResult).title,
        connectorInfo: null,
        fileTypeInfo: null,
        icon: (result as TypeaheadResult).icon || null,
        launchResult: (result as TypeaheadResult).launchResult || null,
      };
    case SummaryQnAEnum.RECENT: {
      const recentContent = result as RecentContent;
      let connectorInfo = null;
      if (
        "details" in recentContent &&
        recentContent.details?.object_details?.[".tag"] === "document" &&
        recentContent.details.object_details.connector_info
      ) {
        connectorInfo = buildConnectorInfo(
          recentContent.details.object_details.connector_info,
        );
      }
      return {
        type: SummaryQnAEnum.RECENT,
        summarizableTag: recentContent.summarizable || null,
        uuid: recentContent.uuid || "",
        url: recentContent.url || null,
        title: recentContent.title,
        connectorInfo,
        fileTypeInfo: null,
        icon: null,
        launchResult: null,
      };
    }
    default:
      summaryQnAType satisfies never;
      logger.error("Unsupported data type", result);
      return null;
  }
};

type RecordTypeTag =
  | "unknown_record_type"
  | "document"
  | "contact"
  | "event"
  | "file"
  | "cloud_app"
  | "workspace"
  | "dash_desktop_application"
  | "dash_desktop_local_file"
  | "browser_tab"
  | "other"
  | "user"
  | "webpage"
  | "relationship";

// exported for testing

export const isRecordTypeTag = (
  tag: dash.RecordType[".tag"] | SurfaceSpecificRecordType[".tag"],
): tag is RecordTypeTag => {
  const validTags: RecordTypeTag[] = [
    "unknown_record_type",
    "document",
    "contact",
    "event",
    "file",
    "cloud_app",
    "workspace",
    "dash_desktop_application",
    "dash_desktop_local_file",
    "webpage",
    "other",
  ];
  return validTags.includes(tag);
};

export const getRecordType = (
  result: dbxApiService.SearchResult,
): RecordTypeTag | undefined => {
  if (result.recordType && result.recordType[".tag"]) {
    const tag = result.recordType[".tag"];
    if (isRecordTypeTag(tag)) {
      return tag;
    }
  }
  return undefined;
};

export const useSummarizableReasons = () => {
  const getSummarizableReason = useCallback(
    (reason: dash.SummarizableType[".tag"] | null | undefined) => {
      switch (reason) {
        case "yes_summarizable":
          return i18n.t("summarizable_reason_yes_summarizable");
        case "not_summarizable_branded_type":
          return i18n.t("summarizable_reason_not_summarizable_branded_type");
        case "not_summarizable_mime_type":
          return i18n.t("summarizable_reason_not_summarizable_mime_type");
        case "not_summarizable_body_missing_empty_file":
          return i18n.t(
            "summarizable_reason_not_summarizable_body_missing_empty_file",
          );
        case "not_summarizable_body_missing_file_size":
          return i18n.t(
            "summarizable_reason_not_summarizable_body_missing_file_size",
          );
        case "not_summarizable_body_missing_unsupported":
          return i18n.t(
            "summarizable_reason_not_summarizable_body_missing_unsupported",
          );
        case "not_summarizable_body_missing_processing_failure":
          return i18n.t(
            "summarizable_reason_not_summarizable_body_missing_processing_failure",
          );
        case "not_summarizable_body_missing_skipped":
          return i18n.t(
            "summarizable_reason_not_summarizable_body_missing_skipped",
          );
        // TODO OSE-4166 look at why typescript hates this i18n
        // case 'not_summarizable_body_missing_other':
        //   return i18n.t(
        //     'summarizable_reason_not_summarizable_body_missing_other',
        //   );
        default:
          return i18n.t("summarizable_reason_not_available");
      }
    },
    [],
  );

  return getSummarizableReason;
};
